import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Redirect } from "react-router-dom";
import { TbPencil, TbSearch, TbUserPlus } from "react-icons/tb";
import { ScreenGreeting } from "../../../Components/Molecules";
import { OperatorsSkeleton } from "../../../Components/Molecules/DashboardSkeleton";
import { useAuth } from "../../../Contexts/AuthContext";
import { useToast } from "../../../Hooks/toast";
import { carteiraUsersErrorMessage, fetchCarteiraUsers } from "../../../Services/carteiraUsersApi";
import { CarteiraUserCreateModal } from "./CarteiraUserCreateModal";

const roleLabel = {
  admin: "Administrador",
  operator: "Operador",
  viewer: "Visualizador",
};

const statusStyles = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  pending: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
  suspended: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
};

const t = (key, fallback) => {
  const value = intl.get(key);
  return value && value !== key ? value : fallback;
};

const formatLastAccess = (value) => {
  if (!value) return t("walletUsers.page.never", "Nunca");
  return new Intl.DateTimeFormat(intl.getInitOptions?.()?.currentLocale || "pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

/** Managers can edit when wallet RBAC id or AccountPersonAccounts personId is present. */
const canManageUserRow = (user, manageSupported) => {
  if (!manageSupported) return false;
  const walletId = Number(user?.id);
  if (Number.isFinite(walletId) && walletId > 0) return true;
  const personId = Number(user?.personId);
  return Number.isFinite(personId) && personId > 0;
};

export const CarteiraUsersPage = () => {
  const { hasPermission, isReadOnly } = useAuth();
  const canManageUsers = hasPermission("operatorscreate");
  const { addToast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [manageSupported, setManageSupported] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setUsersLoading(true);
    (async () => {
      try {
        const result = await fetchCarteiraUsers();
        if (!cancelled) {
          setUsers(result.items);
          setManageSupported(Boolean(result.manageSupported));
        }
      } catch (error) {
        if (!cancelled) {
          setUsers([]);
          setManageSupported(false);
          addToast({
            type: "error",
            title: carteiraUsersErrorMessage(
              error,
              t("walletUsers.page.loadFailed", "Não foi possível carregar os usuários."),
            ),
          });
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, addToast]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        (roleLabel[user.role] ?? user.role).toLowerCase().includes(query) ||
        String(user.status ?? "")
          .toLowerCase()
          .includes(query),
    );
  }, [searchQuery, users]);

  const openCreate = () => {
    setEditingUser(null);
    setCreateModalOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setCreateModalOpen(true);
  };

  const statusLabelFor = (status) => {
    if (status === "active") return t("walletUsers.statusActive", "Ativo");
    if (status === "pending") return t("walletUsers.page.statusPending", "Pendente");
    return t("walletUsers.statusSuspended", "Suspenso");
  };

  if (isReadOnly) {
    return <Redirect to="/" />;
  }

  if (usersLoading) {
    return (
      <div className="profile-page w-full min-w-0 max-w-full">
        <ScreenGreeting
          title={t("walletUsers.page.title", "Usuários")}
          subTitle={t(
            "walletUsers.page.subtitleNoVerify",
            "Adicione pessoas (AccountPerson) e defina permissões da Carteira.",
          )}
        />
        <OperatorsSkeleton />
      </div>
    );
  }

  return (
    <div className="profile-page w-full min-w-0 max-w-full space-y-5">
      <ScreenGreeting
        title={t("walletUsers.page.title", "Usuários")}
        subTitle={t(
          "walletUsers.page.subtitleNoVerify",
          "Adicione pessoas (AccountPerson) e defina permissões da Carteira.",
        )}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-md flex-1">
          <TbSearch
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
            aria-hidden
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t(
              "walletUsers.page.searchPlaceholder",
              "Buscar por nome, e-mail ou perfil",
            )}
            className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
          />
        </div>

        {manageSupported && canManageUsers ? (
          <button type="button" className="btn-primary shrink-0 !h-10" onClick={openCreate}>
            <TbUserPlus size={18} aria-hidden />
            {t("walletUsers.page.addUser", "Adicionar usuário")}
          </button>
        ) : null}
      </div>

      <div className="app-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50/80 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">{t("walletUsers.page.colName", "Nome")}</th>
                <th className="px-4 py-3">{t("walletUsers.page.colEmail", "E-mail")}</th>
                <th className="px-4 py-3">{t("walletUsers.page.colRole", "Perfil")}</th>
                <th className="px-4 py-3">{t("walletUsers.page.colStatus", "Status")}</th>
                <th className="px-4 py-3">{t("walletUsers.page.colLastAccess", "Último acesso")}</th>
                {manageSupported && canManageUsers ? (
                  <th className="px-4 py-3">{t("walletUsers.page.colActions", "Ações")}</th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-brand-dark-border">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={manageSupported && canManageUsers ? 6 : 5} className="px-4 py-8 text-center text-gray-500">
                    {searchQuery.trim()
                      ? t("walletUsers.page.emptySearch", "Nenhum usuário encontrado para a busca.")
                      : t(
                          "walletUsers.page.empty",
                          "Nenhum usuário vinculado a esta conta.",
                        )}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const canAct = canManageUsers && canManageUserRow(user, manageSupported);
                  return (
                    <tr
                      key={`${user.personId ?? "p"}-${user.id || "legacy"}-${user.email}`}
                      className="hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
                    >
                      <td className="px-4 py-3 font-medium text-brand-dark dark:text-gray-100">
                        {user.name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{user.email}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {roleLabel[user.role] ?? user.role}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            statusStyles[user.status] ?? statusStyles.suspended
                          }`}
                        >
                          {statusLabelFor(user.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {formatLastAccess(user.lastAccess)}
                      </td>
                      {manageSupported && canManageUsers ? (
                        <td className="px-4 py-3">
                          {canAct ? (
                            <button
                              type="button"
                              className="btn-outline-icon"
                              aria-label={`Editar ${user.name}`}
                              title={t("walletUsers.page.editUser", "Editar permissões")}
                              onClick={() => openEdit(user)}
                            >
                              <TbPencil className="h-4 w-4" />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {manageSupported && canManageUsers ? (
        <CarteiraUserCreateModal
          open={createModalOpen}
          user={editingUser}
          onClose={() => {
            setCreateModalOpen(false);
            setEditingUser(null);
          }}
          onSaved={() => setRefreshKey((current) => current + 1)}
        />
      ) : null}
    </div>
  );
};

export default CarteiraUsersPage;
